import { LoadingButton } from '@mui/lab'
import { Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, FormControlLabel, Typography } from '@mui/material'
import { useState, useEffect, useRef } from 'react'
import { Link, useLocation, useParams } from 'react-router-dom'
import { track, useInitiatePayloadPayment, useSubmitPayloadPayment } from '../../../../logic'
import { useOptimisticUpdateNotificationAndOrderNotification, useOptimisticUpdatePayloadMetaData } from '../../../../logic/optimisticUpdates'
import { useGetOrder, useGetPayloadMetaData } from '../../../../logic/useApiQuery'
import { NumericFormat } from 'react-number-format'
import { usePayloadProcessingState } from '../../../context/payload-processing-state/PayloadProcessingState'
import { getTransactionsTotal } from '../../../../utils/getTransactionsTotal'
import { formatCurrency } from '../../../../utils/formatCurrency'
import { ErrorDialog } from '../../dialogs/ErrorDialog'
import { PortalOrder } from '../../../../types'
import { useOrganization } from '../../../OrganizationProvider'

export function CashToCloseForm() {
  const { closingId, id } = useParams()
  const { search } = useLocation()
  const { data: orderData } = useGetOrder(closingId ?? '', search ?? '')
  const { CompanyDisplayName } = useOrganization()

  const { optimisticUpdateNotificationAndOrderNotification } = useOptimisticUpdateNotificationAndOrderNotification()
  const { optimisticUpdatePayloadMetaData } = useOptimisticUpdatePayloadMetaData()

  const { mutate: mutateInitiatePayloadPayment, data: initiatePayloadPaymentData } = useInitiatePayloadPayment(closingId ?? '', id ?? '', search ?? '', {
    useErrorBoundary: true,
    onSuccess: () => {
      setPaymentStatus('idle')
    }
  })
  const { mutate: mutateSubmitPayloadPayment } = useSubmitPayloadPayment(closingId ?? '', id ?? '', search ?? '', {
    onSuccess: (data) => {
      if (!id || !closingId) return
      optimisticUpdateNotificationAndOrderNotification({orderKey: closingId, notificationId: id, search, patch: { IsPaymentSubmitted: true } })
      optimisticUpdatePayloadMetaData({orderKey: closingId, notificationId: id, search, patch: { transactionId: data.transactionId, amount: data.amount, createdAt: data.createdAt, accountNumber: data.accountNumber, status: data.status }})
      setPayloadState('completed')
    },
    useErrorBoundary: true
  })

  const { data: transactions } = useGetPayloadMetaData(closingId ?? '', id ?? '', search ?? '')
  const [paymentStatus, setPaymentStatus] = useState<'idle' | 'processing'>('processing')
  const [termsAccepted, setTermsAccepted] = useState<boolean>(false)
  const { setPayloadState } = usePayloadProcessingState()
  const payloadFormRef = useRef(null)
  const hiddenPlaidButtonRef = useRef<HTMLButtonElement>(null)

  const total = getTransactionsTotal(transactions)
  const totalFormatted = formatCurrency(total)
  const [payloadError, setPayloadError] = useState<any>(null)
  const [amount, setAmount] = useState<string | number>(
    Math.max(
      0,
      Number((orderData?.Order as PortalOrder).CashToCloseDetails?.amount) - total //Just saying this is a PortalOrder to satisfy TS for now as AdhocPortalOrder is not relevant here.
    )
  )

  const amountGreaterThanZero = Number(amount) > 0
  const amountLessThan400K = Number(amount) <= 400000

  const hasTransactions = (transactions?.length ?? 0) > 0;
  const [showConfirmAnotherPayment, setShowConfirmAnotherPayment] = useState(false)

  // Initialize Payload form after payment is initiated and token is available
  useEffect(() => {
    const plaidTarget = hiddenPlaidButtonRef.current
    if (initiatePayloadPaymentData?.clientToken && payloadFormRef.current && plaidTarget && amountGreaterThanZero && amountLessThan400K && termsAccepted) {
      Payload(initiatePayloadPaymentData?.clientToken)

      const form = new Payload.Form({
        form: payloadFormRef.current,
        preventDefaultOnSubmit: true
      })

      form.plaid(plaidTarget)
      //The following events are grabbed from here, but this documentation does not include 'closed'.
      //https://docs.payload.com/ui/payloadjs/secure-input/options/

      form.on('success', function(event: any) {
        mutateSubmitPayloadPayment({payloadTransactionId: event.transaction_id})
        mutateInitiatePayloadPayment(undefined)
      })

      form.on('processing', function() {
        setPayloadState('processing')
      })

      form.on('closed', function() {
        setPaymentStatus('idle')
      })
      form.on('error', function(error: any) {
        setPayloadError(error)
      })
    }
  }, [initiatePayloadPaymentData?.clientToken, mutateSubmitPayloadPayment, mutateInitiatePayloadPayment, setPayloadState, amountGreaterThanZero, amountLessThan400K, termsAccepted])

  useEffect(() => {
    mutateInitiatePayloadPayment(undefined)
  }, [mutateInitiatePayloadPayment])

  const handleCompletePaymentClicked = () => {
    if (amountGreaterThanZero && amountLessThan400K) {
      if (hasTransactions) {
        setShowConfirmAnotherPayment(true)
      } else {
        setPaymentStatus('processing')
        if (hiddenPlaidButtonRef.current) {
          hiddenPlaidButtonRef.current.click()
        }
      }
    }
  }

  const handleConfirmAnotherPayment = () => {
    setPaymentStatus('processing')
    setShowConfirmAnotherPayment(false)
    if (hiddenPlaidButtonRef.current) {
      hiddenPlaidButtonRef.current.click()
    }
  }

  const paymentButtonText = hasTransactions ? 'Complete another payment' : 'Complete a payment'
  const paymentButtonVariant: 'text' | 'contained' = hasTransactions ? 'text' : 'contained'

  if (payloadError) {
    track('error', 'payload form', {}, {
      message: JSON.stringify(payloadError),
      component: 'CashToCloseForm',
    })
    return (
      <ErrorDialog titleText='Payment could not be processed'>
        <>
          We couldn't complete your payment from this account at this time.  Because closing funds are time sensitive, please try a different account or contact {CompanyDisplayName} for alternative payment instructions.  Contact information is available in the{' '}
          <Link to={`/closing/${closingId}/closing-contacts${search}`}>contacts section</Link>.
        </>
      </ErrorDialog>
    )
  }

  return (
    <>
      <>
        <form id="payload" ref={payloadFormRef} pl-form="payment"
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}>
          <Box display="flex" flexDirection="column" gap={3} alignItems="center">
            <Box alignItems="center" display='flex' flexDirection='column'>
              <Box width={'150px'}>
                <Typography variant="subtitle2" gutterBottom>Amount</Typography>
                <NumericFormat
                  allowNegative={false}
                  allowLeadingZeros={false}
                  value={amount}
                  onValueChange={(values) => {
                    setAmount(values.value)
                  }}
                  thousandSeparator={true}
                  prefix="$"
                  pl-input="amount"
                  style={{
                    width: '100%',
                    minHeight: '40px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    padding: '8px',
                    fontSize: '1rem',
                    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                  }}
                />
              </Box>
              {!amountGreaterThanZero && <Typography color='red' fontSize='15px'>Amount must be greater than $0</Typography>}
              {!amountLessThan400K && <Typography color='red' fontSize='15px'>Amount must be less than $400,000</Typography>}
            </Box>

            <FormControlLabel
              control={
                <Checkbox
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                />
              }
              label={
                <Typography fontSize='15px'>
                  By completing a payment you confirm that you have read, accepted and agreed to be bound by Payload's{' '}
                  <a
                    href='https://payload.com/terms'
                    target='_blank'
                    rel='noopener noreferrer'

                    style={{ color: '#1976d2', textDecoration: 'underline' }}
                  >Terms of Use</a>
                    .
                </Typography>
              }
              sx={{ marginX: 4 }}
            />

            <Box alignItems="center" display='flex' flexDirection='column'>
              <LoadingButton
                disabled={!amountGreaterThanZero || !amountLessThan400K || !termsAccepted}
                loading={paymentStatus === 'processing'}
                loadingPosition="start"
                startIcon={ paymentStatus === 'processing' ? <span /> : null}
                onClick={handleCompletePaymentClicked}
                variant={paymentButtonVariant}
                color="primary"
                type="button"
                sx={paymentStatus === 'processing' ? { pl: 4 } : {}}
              >
                {paymentButtonText}
              </LoadingButton>
            </Box>
          </Box>
          <button ref={hiddenPlaidButtonRef} type="button" style={{ display: 'none' }} />
        </form>
      </>
      <Dialog open={showConfirmAnotherPayment} onClose={() => setShowConfirmAnotherPayment(false)}>
        <DialogTitle>Submit Additional Payment</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You've already submitted {totalFormatted} toward your cash to close. Would you like to submit an additional payment?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowConfirmAnotherPayment(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleConfirmAnotherPayment}>Submit additional payment</Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

// Add TypeScript declaration for Payload as a global variable
declare global {
  var Payload: any
}
